<script setup lang="ts">
import { computed, onMounted, shallowRef } from 'vue';
import MarketplaceTopOfBookWatchlistManager from '@/modules/marketplace/components/MarketplaceTopOfBookWatchlistManager.vue';
import { TopOfBookWatchlist } from '@/modules/marketplace/models';
import { storeGetters } from '@/store/getters';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';

const emit = defineEmits<{ (e: 'change', payload: string | null): void }>();

const props = withDefaults(defineProps<{ selectedDisplayId?: string | null }>(), {
  selectedDisplayId: null,
});

const marketplaceApi = useMarketplaceApiService();

const watchlists = shallowRef<TopOfBookWatchlist[]>([]);
const showWatchlistManager = shallowRef(false);

const hasTraderViewerRole = computed(() => storeGetters.hasTraderViewerRole);

const selectedTabIndex = computed(() =>
  props.selectedDisplayId === null
    ? 0
    : watchlists.value.findIndex((watchlist) => watchlist.displayId === props.selectedDisplayId) + 1
);

async function fetchWatchlists(): Promise<void> {
  watchlists.value = await marketplaceApi.fetchWatchlists();
  if (!watchlists.value.some((watchlist) => watchlist.displayId === props.selectedDisplayId)) {
    emit('change', null);
  }
}

function openDialog() {
  showWatchlistManager.value = true;
}

onMounted(() => {
  void fetchWatchlists();
});
</script>

<template>
  <VContainer class="py-0" fluid>
    <div class="mt-4 d-flex gap-1">
      <VBtn
        color="secondary"
        data-test-watchlist-tabs="show-watchlist-manager"
        :disabled="!hasTraderViewerRole"
        @click="openDialog()"
      >
        Watchlists
      </VBtn>
      <slot name="additional-buttons"></slot>
    </div>
    <VRow class="mt-2 mb-4 d-flex align-center" no-gutters>
      <VTabs background-color="transparent" show-arrows :value="selectedTabIndex">
        <VTab data-test-watchlist-tab-name="all" @change="emit('change', null)"> All</VTab>

        <VTab
          v-for="watchlist in watchlists"
          :key="watchlist.displayId"
          :data-test-watchlist-tab-name="watchlist.name"
          :value="watchlist.displayId"
          @change="emit('change', watchlist.displayId)"
        >
          {{ watchlist.name }}
        </VTab>
      </VTabs>
    </VRow>

    <MarketplaceTopOfBookWatchlistManager
      v-if="showWatchlistManager"
      data-test-watchlist-manager="manager-dialog"
      :watchlists="watchlists"
      @close-modal="showWatchlistManager = false"
      @refresh-watchlists="fetchWatchlists"
    />
  </VContainer>
</template>

<style lang="scss" scoped>
.gap-1 {
  gap: 1rem;
}
</style>
